import { Injectable } from '@angular/core';
import { Feature, Component, COMPONENT_TO_FEATURE_MAP, FEATURE_TO_FEATURE_MAP } from '../models/feature.model';

export type Role = string; // Make role dynamic instead of static types
export { Feature } from '../models/feature.model'; // Re-export Feature for backward compatibility

const STORAGE_KEY = 'userAllowedModules';
const USER_ROLE_KEY = 'userRole';
const USER_ALLOWED_HUBS_KEY = 'userAllowedHubs';

@Injectable({ providedIn: 'root' })
export class RoleAccessService {
  private userAllowedModules: string[] = [];
  private userRole: Role = '';
  private userAllowedHubs: string[] = [];

  constructor() {
    this.loadUserAccess();
  }

  private loadUserAccess() {
    try {
      const storedModules = localStorage.getItem(STORAGE_KEY);
      const storedRole = localStorage.getItem(USER_ROLE_KEY);
      const storedHubs = localStorage.getItem(USER_ALLOWED_HUBS_KEY);

      const parsedModules = this.safeJsonParse(storedModules);
      if (parsedModules && Array.isArray(parsedModules)) {
        this.userAllowedModules = parsedModules;
      }

      if (storedRole && storedRole !== 'null') {
        this.userRole = storedRole;
      }

      const parsedHubs = this.safeJsonParse(storedHubs);
      if (parsedHubs && Array.isArray(parsedHubs)) {
        this.userAllowedHubs = parsedHubs;
      }
    } catch (error) {
      console.error('Error loading user access from localStorage:', error);
      // Reset to default values if there's an error
      this.userAllowedModules = [];
      this.userRole = '';
      this.userAllowedHubs = [];
    }
  }

  private saveUserAccess() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.userAllowedModules));
      localStorage.setItem(USER_ROLE_KEY, this.userRole);
      localStorage.setItem(USER_ALLOWED_HUBS_KEY, JSON.stringify(this.userAllowedHubs));
    } catch (error) {
      console.error('Error saving user access to localStorage:', error);
    }
  }

  private safeJsonParse(value: string | null): any {
    if (!value || value === 'null' || value === 'undefined') {
      return null;
    }
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error('Error parsing JSON:', error);
      return null;
    }
  }

  // Set user access from login response
  setUserAccess(role: Role, allowedModules: string[], allowedHubs?: string[]) {
    this.userRole = role;
    this.userAllowedModules = allowedModules;
    this.userAllowedHubs = allowedHubs || [];

    // If user is admin, automatically grant access to User Management
    if (this.isAdminUser(role)) {
      this.addUserManagementAccess();
    }

    this.saveUserAccess();
  }

  // Check if user is admin
  private isAdminUser(role: Role): boolean {
    return role === 'admin' ||
           Boolean(role && role.toLowerCase().includes('admin'));
  }

  // Add User Management access for admin users
  private addUserManagementAccess(): void {
    // Only add User Management access if the user is admin and doesn't already have it
    if (this.isAdminUser(this.userRole) && !this.userAllowedModules.includes(Feature.USER_MANAGEMENT)) {
      this.userAllowedModules.push(Feature.USER_MANAGEMENT);
    }
  }

  // Clear user access (logout)
  clearUserAccess() {
    this.userAllowedModules = [];
    this.userRole = '';
    this.userAllowedHubs = [];
    this.saveUserAccess();
  }

  // Check if user can access a specific feature
  canAccess(feature: Feature): boolean {
    // Check if user has access to the feature
    const hasFeatureAccess = this.userAllowedModules.includes(feature);

    // Admin users get automatic access to User Management
    if (feature === Feature.USER_MANAGEMENT && this.isAdminUser(this.userRole)) {
      return true;
    }

    return hasFeatureAccess;
  }

  // Check if user can access a specific component
  canAccessComponent(componentName: string): boolean {
    const feature = COMPONENT_TO_FEATURE_MAP[componentName as Component];
    if (!feature) {
      console.warn(`No feature mapping found for component: ${componentName}`);
      return false;
    }
    return this.canAccess(feature);
  }

  // Get user role
  getUserRole(): Role {
    return this.userRole;
  }

  // Get user allowed modules
  getUserAllowedModules(): string[] {
    return [...this.userAllowedModules];
  }

  // Get user allowed features
  getUserAllowedFeatures(): Feature[] {
    return this.userAllowedModules.filter(module =>
      Object.values(Feature).includes(module as Feature)
    ) as Feature[];
  }

  // Get user allowed hubs
  getUserAllowedHubs(): string[] {
    return [...this.userAllowedHubs];
  }

  // Check if user can access a specific hub
  canAccessHub(hubName: string): boolean {
    return this.userAllowedHubs.includes(hubName);
  }

  // Set user allowed hubs
  setUserAllowedHubs(allowedHubs: string[]) {
    this.userAllowedHubs = allowedHubs;
    this.saveUserAccess();
  }

  // Add a hub to user's allowed hubs
  addUserHub(hubName: string) {
    if (!this.userAllowedHubs.includes(hubName)) {
      this.userAllowedHubs.push(hubName);
      this.saveUserAccess();
    }
  }

  // Remove a hub from user's allowed hubs
  removeUserHub(hubName: string) {
    const index = this.userAllowedHubs.indexOf(hubName);
    if (index > -1) {
      this.userAllowedHubs.splice(index, 1);
      this.saveUserAccess();
    }
  }

  // Check if user has any hub access
  hasAnyHubAccess(): boolean {
    return this.userAllowedHubs.length > 0;
  }

  // Get component to feature mapping
  getComponentToFeatureMap(): Record<string, Feature> {
    return COMPONENT_TO_FEATURE_MAP;
  }

  // Get feature to feature mapping
  getFeatureToFeatureMap(): Record<string, Feature> {
    return FEATURE_TO_FEATURE_MAP;
  }

  // Check if user has any access
  hasAnyAccess(): boolean {
    return this.userAllowedModules.length > 0 || this.userAllowedHubs.length > 0;
  }

  // Get user access summary
  getUserAccessSummary(): {
    role: Role;
    allowedModules: string[];
    allowedFeatures: Feature[];
    allowedHubs: string[];
    hasAccess: boolean;
    hasHubAccess: boolean;
  } {
    return {
      role: this.userRole,
      allowedModules: this.userAllowedModules,
      allowedFeatures: this.getUserAllowedFeatures(),
      allowedHubs: this.userAllowedHubs,
      hasAccess: this.hasAnyAccess(),
      hasHubAccess: this.hasAnyHubAccess()
    };
  }
}
